import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  getMunicipalityById,
  getMunicipalityCategories,
  getMunicipalityDistricts,
  getMunicipalityList,
  getMunicipalityPermitMetadata,
  getMunicipalityStates,
  getMunicipalityStatuses,
  getMunicipalityTypes,
  saveMunicipalityRegistration,
  seedMunicipalityDefaults,
  triStateDbToList,
  type MunicipalityListRow,
  type MunicipalityRecord,
  type SelectOption,
  type PermitMeta,
} from '../../components/Admin/MunicipalityRegistration/services/municipalityRegistration.service';

// ─── State types ──────────────────────────────────────────────────────────────

type AsyncStatus = 'loading' | 'succeeded' | 'failed' | false;

export interface MunicipalityListQuery {
  rows: number;
  page: number;
  sortBy: string;
  orderBy: number | string;
  search: string;
}

export interface MunicipalityRegistrationState {
  list: {
    rows: MunicipalityListRow[];
    totalCount: number;
    status: AsyncStatus;
    error: string | null;
    query: MunicipalityListQuery;
  };
  details: {
    byId: Record<string | number, MunicipalityRecord>;
    status: AsyncStatus;
    error: string | null;
  };
  meta: {
    states: SelectOption[];
    districts: SelectOption[];
    types: SelectOption[];
    statuses: SelectOption[];
    categories: SelectOption[];
    permitMeta: PermitMeta | null;
    status: AsyncStatus;
    error: string | null;
  };
  save: {
    status: AsyncStatus;
    error: string | null;
  };
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: MunicipalityRegistrationState = {
  list: {
    rows: [],
    totalCount: 0,
    status: false,
    error: null,
    query: { rows: 10, page: 0, sortBy: '', orderBy: 1, search: '' },
  },
  details: { byId: {}, status: false, error: null },
  meta: {
    states: [],
    districts: [],
    types: [],
    statuses: [],
    categories: [],
    permitMeta: null,
    status: false,
    error: null,
  },
  save: { status: false, error: null },
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchMunicipalityList = createAsyncThunk(
  'municipalityRegistration/fetchList',
  async (
    query: Partial<MunicipalityListQuery> | undefined,
    { getState, rejectWithValue }
  ) => {
    const state = getState() as {
      municipalityRegistration: MunicipalityRegistrationState;
    };
    const q = {
      ...state.municipalityRegistration.list.query,
      ...(query || {}),
    };
    try {
      const res = await getMunicipalityList(
        q.rows,
        q.page,
        q.sortBy,
        q.orderBy,
        q.search
      );
      return { ...res, query: q };
    } catch (e) {
      return rejectWithValue((e as Error)?.message || 'Failed to fetch list');
    }
  }
);

export const fetchMunicipalityById = createAsyncThunk(
  'municipalityRegistration/fetchById',
  async (
    { id, force = false }: { id: string | number; force?: boolean },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as {
        municipalityRegistration: MunicipalityRegistrationState;
      };
      const existing = state.municipalityRegistration.details.byId?.[id];
      if (existing && !force) return { id, data: existing, fromCache: true };
      const res = await getMunicipalityById(id);
      return { id, data: res };
    } catch (e) {
      return rejectWithValue(
        (e as Error)?.message || 'Failed to fetch details'
      );
    }
  }
);

export const fetchMunicipalityMeta = createAsyncThunk(
  'municipalityRegistration/fetchMeta',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as {
      municipalityRegistration: MunicipalityRegistrationState;
    };
    const meta = state.municipalityRegistration.meta;
    if (
      meta.states?.length &&
      meta.types?.length &&
      meta.statuses?.length &&
      meta.permitMeta
    ) {
      return rejectWithValue('Meta already fetched');
    }
    try {
      await seedMunicipalityDefaults().catch(() => null);

      const fetchOrEmpty = async <T>(
        fn: () => Promise<T>,
        fallback: T
      ): Promise<T> => {
        try {
          return await fn();
        } catch {
          return fallback;
        }
      };

      const [states, districts, types, statuses, categories, permitMeta] =
        await Promise.all([
          fetchOrEmpty(() => getMunicipalityStates(), []),
          fetchOrEmpty(() => getMunicipalityDistricts(), []),
          fetchOrEmpty(() => getMunicipalityTypes(), []),
          fetchOrEmpty(() => getMunicipalityStatuses(), []),
          fetchOrEmpty(() => getMunicipalityCategories(), []),
          fetchOrEmpty(
            () => getMunicipalityPermitMetadata(),
            null as PermitMeta | null
          ),
        ]);
      return { states, districts, types, statuses, categories, permitMeta };
    } catch (e) {
      return rejectWithValue((e as Error)?.message || 'Failed to fetch meta');
    }
  }
);

export const saveMunicipality = createAsyncThunk(
  'municipalityRegistration/save',
  async (
    {
      id,
      payload,
      notes = [],
      pendingDocuments = [],
      skipRecordUpdate = false,
    }: {
      id?: string | number | null;
      payload: Partial<MunicipalityRecord>;
      notes?: import('../../components/Admin/MunicipalityRegistration/interfaces/municipalityRegistration.interface').MunicipalityNote[];
      pendingDocuments?: File[];
      skipRecordUpdate?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await saveMunicipalityRegistration({
        id,
        payload,
        notes,
        pendingDocuments,
        skipRecordUpdate,
      });
      return { id: res?.id ?? id, data: res };
    } catch (e) {
      return rejectWithValue((e as Error)?.message || 'Failed to save');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const municipalityRegistrationSlice = createSlice({
  name: 'municipalityRegistration',
  initialState,
  reducers: {
    clearMunicipalityDetails(state) {
      state.details.byId = {};
      state.details.status = false;
      state.details.error = null;
    },
    setMunicipalityListQuery(
      state,
      { payload }: PayloadAction<Partial<MunicipalityListQuery>>
    ) {
      state.list.query = { ...state.list.query, ...(payload || {}) };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMunicipalityList.pending, state => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchMunicipalityList.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.error = null;
        state.list.rows = action.payload?.records || [];
        state.list.totalCount = action.payload?.totalCount || 0;
        state.list.query = action.payload?.query || state.list.query;
      })
      .addCase(fetchMunicipalityList.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error =
          (action.payload as string) || action.error?.message || null;
      })
      .addCase(fetchMunicipalityById.pending, state => {
        state.details.status = 'loading';
        state.details.error = null;
      })
      .addCase(fetchMunicipalityById.fulfilled, (state, action) => {
        state.details.status = 'succeeded';
        state.details.error = null;
        const id = action.payload?.id;
        if (id != null)
          state.details.byId[id] =
            action.payload?.data || ({} as MunicipalityRecord);
      })
      .addCase(fetchMunicipalityById.rejected, (state, action) => {
        state.details.status = 'failed';
        state.details.error =
          (action.payload as string) || action.error?.message || null;
      })
      .addCase(fetchMunicipalityMeta.pending, state => {
        state.meta.status = 'loading';
        state.meta.error = null;
      })
      .addCase(fetchMunicipalityMeta.fulfilled, (state, action) => {
        state.meta.status = 'succeeded';
        state.meta.error = null;
        state.meta.states = action.payload?.states || [];
        state.meta.districts = action.payload?.districts || [];
        state.meta.types = action.payload?.types || [];
        state.meta.statuses = action.payload?.statuses || [];
        state.meta.categories = action.payload?.categories || [];
        state.meta.permitMeta = action.payload?.permitMeta || null;
      })
      .addCase(fetchMunicipalityMeta.rejected, (state, action) => {
        if (action.payload === 'Meta already fetched') {
          state.meta.status = 'succeeded';
        } else {
          state.meta.status = 'failed';
          state.meta.error =
            (action.payload as string) || action.error?.message || null;
        }
      })
      .addCase(saveMunicipality.pending, state => {
        state.save.status = 'loading';
        state.save.error = null;
      })
      .addCase(saveMunicipality.fulfilled, (state, action) => {
        state.save.status = 'succeeded';
        state.save.error = null;
        const id = action.payload?.id;
        const data = action.payload?.data as MunicipalityRecord | undefined;
        if (id != null && data) {
          const prev = state.details.byId[id as string | number];
          state.details.byId[id as string | number] = {
            ...prev,
            ...data,
            type: data.type ?? prev?.type,
            status: data.status ?? prev?.status,
            state: data.state ?? prev?.state,
            notes: data.notes?.length ? data.notes : (prev?.notes ?? []),
            documents: data.documents?.length
              ? data.documents
              : (prev?.documents ?? []),
            typeLabel:
              data.typeLabel ??
              (data.type as { label?: string })?.label ??
              prev?.typeLabel,
            statusLabel:
              data.statusLabel ??
              (data.status as { status?: string })?.status ??
              (data.status as { label?: string })?.label ??
              prev?.statusLabel,
            stateLabel: data.stateLabel ?? prev?.stateLabel,
          };
          const idx = state.list.rows?.findIndex(r => r.id === id);
          if (idx >= 0) {
            state.list.rows[idx] = {
              ...state.list.rows[idx],
              id: data.id,
              state:
                data.stateLabel ||
                (data.state as { state_code?: string })?.state_code ||
                state.list.rows[idx].state,
              municipality:
                data.municipalityName || state.list.rows[idx].municipality,
              county: data.county || state.list.rows[idx].county,
              type:
                data.typeLabel ||
                (data.type as { label?: string })?.label ||
                state.list.rows[idx].type,
              certNumber:
                data.documentNumber || state.list.rows[idx].certNumber,
              expirationDate:
                data.expiryDate || state.list.rows[idx].expirationDate,
              permitRequired: triStateDbToList(data.permitRequired),
              registrationRequired: triStateDbToList(data.registrationRequired),
              status:
                data.statusLabel ||
                (data.status as { status?: string })?.status ||
                (data.status as { label?: string })?.label ||
                state.list.rows[idx].status,
              district: data.district || state.list.rows[idx].district,
            };
          }
        }
      })
      .addCase(saveMunicipality.rejected, (state, action) => {
        state.save.status = 'failed';
        state.save.error =
          (action.payload as string) || action.error?.message || null;
      });
  },
});

export const { clearMunicipalityDetails, setMunicipalityListQuery } =
  municipalityRegistrationSlice.actions;

export const municipalityRegistrationReducer =
  municipalityRegistrationSlice.reducer;
